/**
 * Centralized API utilities for making fetch requests
 * This follows the service integration standards with proper error handling
 */

// Base API URL - ensure no whitespace
const API_BASE_URL = window.location.hostname.includes('explanatum.co.uk')
  ? 'https://api.explanatum.co.uk'
  : (process.env.REACT_APP_API_URL?.trim() || 'http://localhost:5000').trim();

/**
 * Make a fetch request with standardized error handling
 */
export async function fetchApi(endpoint: string, options: RequestInit = {}) {
  try {
    // Ensure the endpoint starts with a slash and is trimmed
    const cleanEndpoint = endpoint.trim();
    const normalizedEndpoint = cleanEndpoint.startsWith('/') ? cleanEndpoint : `/${cleanEndpoint}`;
    
    // Create a clean URL with no spaces
    const url = `${API_BASE_URL}${normalizedEndpoint}`;
    
    // Make the fetch request
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    // Handle non-OK responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Unknown error occurred',
      }));
      throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
    }

    // Parse and return the JSON response
    return await response.json();
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
}

/**
 * Guest checkout API call
 */
export async function createGuestCheckout(email: string, planId: string, token?: string) {
  const headers: HeadersInit = {};
  
  // Add auth token if available
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetchApi('api/payment/guest-checkout', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      email,
      planId
    }),
  });
}

/**
 * Get subscription details for a user
 */
export async function getSubscriptionDetails(userId: string, token: string) {
  return fetchApi(`api/payment/subscription/${userId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
}

/**
 * Cancel a subscription
 */
export async function cancelSubscription(subscriptionId: string, token: string) {
  return fetchApi('api/payment/subscription/cancel', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      subscriptionId
    }),
  });
}

/**
 * Reactivate a subscription
 */
export async function reactivateSubscription(subscriptionId: string, token: string) {
  return fetchApi('api/payment/subscription/reactivate', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      subscriptionId
    }),
  });
}

/**
 * Create a billing portal session
 */
export async function createBillingPortalSession(returnUrl: string, token: string) {
  return fetchApi('api/payment/create-portal-session', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      returnUrl
    }),
  });
}
